'use client';

import { API_ROUTES, ApiError } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { cn } from '@shared/components/shadcn';
import { createDebug } from '@shared/debug';
import { useLongPoll } from '@shared/hooks';
import { Spinner } from '@shared/icons';
import { useFullStoryTrackEvent } from '@shared/tracking';
import { getBrand, isKenoGoBrand, setAwaitingRegistrationSuccessRedirect, useClientConfig, type Brand } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useMultistepForm } from '../../../form-controls/MultistepForm/hooks/useMultistepForm';
import { useKycDialog } from '../../../global-dialogs/components/KycDialog/context/KycDialogContext';
import { discardOrphanRegistrationAuthReturn, settleRegistrationAuthResume, shouldResumeRegistrationAfterAuth } from '../hooks/registrationAuthResume';
import { usePlayerRegistration } from '../hooks/usePlayerRegistration';
import { inferRegistrationFailureKind, setRegistrationFailureKind } from '../utils/registrationFailureKind';

type LoadingSuccessStepForm = Pick<UseFormReturn<Record<string, unknown>>, 'getValues' | 'trigger'>;

type LoadingSuccessStepProps = {
  form: LoadingSuccessStepForm;
  brandOverride?: Brand;
};

type PromptDialogResponse = {
  id: string;
  messageType: 'IDENTITY_VERIFICATION_UPLOAD' | 'GREEN_ID_DIALOG' | 'DEPOSIT_LIMITS' | 'NO_DIALOG_TO_SHOW';
};

// Debug utility for logging
const debug = createDebug({ isEnabled: true });

function LoadingSuccessStep({ form, brandOverride }: LoadingSuccessStepProps) {
  const { setStatus, skipToStep } = useMultistepForm();
  const { submitRegistration, completeRegistrationAfterAuth } = usePlayerRegistration();
  const { update } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const hasRegisteredRef = useRef(false);
  const [isPollingEnabled, setIsPollingEnabled] = useState(false);
  const handledMessageTypeRef = useRef<string | null>(null);
  const config = useClientConfig();
  const brand = getBrand(brandOverride);
  const isKenoGo = isKenoGoBrand(brand);
  // Temporarily disabled for release — re-enable with GREEN_ID_DIALOG handler below
  // const isGreenIdSupported = getSiteConfig().KYC_VERIFICATION.methods.some(method => method.name === 'greenid-fallback');
  const t = useTranslations('register.screens.loading');
  const { openKycDialog } = useKycDialog();
  const fullStoryTrackEvent = useFullStoryTrackEvent();

  // Check if there's an offer (bundle code) in URL params
  const bundleCode = searchParams.get('code') || '';
  const hasOffer = Boolean(bundleCode);

  // State for rotating messages and transitions
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [isRegistrationSubmitted, setIsRegistrationSubmitted] = useState(false);
  const [isMessageVisible, setIsMessageVisible] = useState(true);
  const messageStartTimeRef = useRef<Record<number, number>>({});
  const pollingStartTimeRef = useRef<number | null>(null);
  const MESSAGE_DISPLAY_DURATION = 1500; // 1.5 seconds
  const TRANSITION_DURATION = 700; // 700ms fade transition

  useEffect(() => {
    const performRegistration = async () => {
      // Prevent multiple registrations
      if (hasRegisteredRef.current) {
        return;
      }

      try {
        hasRegisteredRef.current = true;

        const isResumingAfterAuth = shouldResumeRegistrationAfterAuth();

        if (!isResumingAfterAuth) {
          // Final validation check before submission
          // Skip productPreferences and channelPreferences validation if user skipped them (empty arrays)
          const productPreferences = form.getValues('productPreferences') as string[] | undefined;
          const channelPreferences = form.getValues('channelPreferences') as string[] | undefined;
          const hasSkippedProductPreferences = !productPreferences || productPreferences.length === 0;
          const hasSkippedChannelPreferences = !channelPreferences || channelPreferences.length === 0;

          const fieldsToValidate =
            hasSkippedProductPreferences || hasSkippedChannelPreferences ? ['createAccount', 'personalDetails', 'address', 'mobileNumber', 'depositLimits'] : undefined; // undefined means validate all fields

          const isValid = await form.trigger(fieldsToValidate);
          if (!isValid) {
            setStatus('error');
            return;
          }
        }

        if (isResumingAfterAuth) {
          const outcome = await settleRegistrationAuthResume(update);

          if (outcome === 'discard') {
            discardOrphanRegistrationAuthReturn(href => router.replace(href), searchParams);
            hasRegisteredRef.current = false;
            setStatus('');
            skipToStep('create-account');
            return;
          }

          if (outcome === 'noop') {
            hasRegisteredRef.current = false;
            setStatus('');
            skipToStep('create-account');
            return;
          }

          await completeRegistrationAfterAuth();
        } else {
          const formData = form.getValues();

          // Call player registration API (validation happens within submitRegistration)
          const registrationResponse = await submitRegistration(formData);

          if (registrationResponse === null) {
            return;
          }
        }

        setAwaitingRegistrationSuccessRedirect(true);

        // Mark registration as submitted
        setIsRegistrationSubmitted(true);

        // Registration context is already stored in form data from useStepsConfig

        // Start polling for GBG integration
        // Don't set status to 'completed' yet - wait for GBG result
        pollingStartTimeRef.current = Date.now();
        setIsPollingEnabled(true);
      } catch (error) {
        // Stop polling if it was enabled (e.g., if error occurs after registration succeeds but profile fetch fails)
        setIsPollingEnabled(false);

        const apiError = error as ApiError;
        setRegistrationFailureKind(inferRegistrationFailureKind(error, apiError instanceof ApiError ? apiError.getStatus() : undefined));

        setStatus('error');
        skipToStep('error');
      }
    };

    performRegistration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  // Track when message 1 starts showing
  useEffect(() => {
    if (currentMessageIndex === 0 && !messageStartTimeRef.current[0]) {
      messageStartTimeRef.current[0] = Date.now();
    }
  }, [currentMessageIndex]);

  // Advance to message 2 when registration is submitted (UK only)
  // Ensures message 1 is visible for at least MESSAGE_DISPLAY_DURATION total
  useEffect(() => {
    if (isKenoGo) return;
    if (isRegistrationSubmitted && currentMessageIndex === 0) {
      const elapsed = Date.now() - (messageStartTimeRef.current[0] || Date.now());
      // If the request took longer than the display duration, transition immediately
      // Otherwise, wait for remaining time to ensure minimum display duration
      const remaining = Math.max(0, MESSAGE_DISPLAY_DURATION - elapsed);

      const timer = setTimeout(() => {
        setIsMessageVisible(false);
        setTimeout(() => {
          setCurrentMessageIndex(1);
          setIsMessageVisible(true);
          messageStartTimeRef.current[1] = Date.now();
        }, TRANSITION_DURATION);
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [isRegistrationSubmitted, currentMessageIndex, isKenoGo]);

  // Keep message 2 ("Verifying your identity...") visible during polling
  // Message 2 should stay visible while polling is active

  // Long polling for GBG integration
  // retryAttempts: 3 means limit total polling attempts to 3 (successful + failed)
  // The hook will automatically stop polling when the limit is reached
  const {
    data: promptDialogData,
    error: promptDialogError,
    hasReachedLimit,
  } = useLongPoll<PromptDialogResponse>(API_ROUTES.PLAYER_KYC.PROMPT_DIALOG, {
    enabled: isPollingEnabled,
    credentials: true,
    intervalMs: 500,
    queryOptions: {
      gcTime: 0, // Don't cache (previously cacheTime)
      staleTime: 0, // Always consider data stale
      retry: false, // Don't retry - we handle retries via long polling
    },
    retryAttempts: 3,
  });

  // Track when account becomes ready
  const [isAccountReady, setIsAccountReady] = useState(false);

  // Advance to message 3 ("Setting up your offer....") after polling finishes (UK only)
  // Only show message 3 if there's an offer (bundle code)
  // Ensures message 2 is visible for at least MESSAGE_DISPLAY_DURATION during polling
  useEffect(() => {
    if (isKenoGo) return;
    // Only advance to message 3 when polling has finished, account is ready, and there's an offer
    if (currentMessageIndex === 1 && isAccountReady && !isPollingEnabled && hasOffer) {
      const elapsed = Date.now() - (messageStartTimeRef.current[1] || Date.now());
      // Ensure message 2 shows for at least 1.5s total (during polling)
      const remaining = Math.max(0, MESSAGE_DISPLAY_DURATION - elapsed);

      const timer = setTimeout(() => {
        setIsMessageVisible(false);
        setTimeout(() => {
          // Set start time before state update to ensure it's available when useEffect runs
          const message3StartTime = Date.now();
          messageStartTimeRef.current[2] = message3StartTime;
          setCurrentMessageIndex(2);
          setIsMessageVisible(true);
        }, TRANSITION_DURATION);
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, isAccountReady, isPollingEnabled, hasOffer, isKenoGo]);

  // Track when message 3 should trigger redirect
  const [shouldRedirect, setShouldRedirect] = useState(false);

  // Transition to SuccessStep after message 3 has been showing for the display duration (if there's an offer)
  // Or directly after message 2 if there's no offer (UK only)
  useEffect(() => {
    if (isKenoGo) return;
    // If there's an offer, wait for message 3 to show for the display duration
    if (hasOffer && currentMessageIndex === 2 && isAccountReady && !isPollingEnabled && messageStartTimeRef.current[2]) {
      const elapsed = Date.now() - messageStartTimeRef.current[2];
      // Ensure message 3 shows for at least the display duration before transitioning to SuccessStep
      const remaining = Math.max(0, MESSAGE_DISPLAY_DURATION - elapsed);

      const timer = setTimeout(() => {
        // Transition to SuccessStep component (which has logo, blings, and success message)
        setShouldRedirect(true);
      }, remaining);

      return () => clearTimeout(timer);
    }

    // If there's no offer, skip message 3 and redirect after message 2 has shown for the display duration
    if (!hasOffer && currentMessageIndex === 1 && isAccountReady && !isPollingEnabled) {
      const elapsed = Date.now() - (messageStartTimeRef.current[1] || Date.now());
      // Ensure message 2 shows for at least the display duration before transitioning to SuccessStep
      const remaining = Math.max(0, MESSAGE_DISPLAY_DURATION - elapsed);

      const timer = setTimeout(() => {
        // Transition to SuccessStep component (which has logo, blings, and success message)
        setShouldRedirect(true);
      }, remaining);

      return () => clearTimeout(timer);
    }
  }, [currentMessageIndex, isAccountReady, isPollingEnabled, hasOffer, isKenoGo]);

  // Redirect for KenoGO once the account is ready.
  useEffect(() => {
    if (isKenoGo && isAccountReady && !isPollingEnabled) {
      setShouldRedirect(true);
    }
  }, [isKenoGo, isAccountReady, isPollingEnabled]);

  // Handle transition to SuccessStep after message 3 has been shown
  useEffect(() => {
    if (shouldRedirect) {
      // Set status to completed to show SuccessStep component (which has logo and blings)
      setStatus('completed');
    }
  }, [shouldRedirect, setStatus]);

  // Handle prompt dialog responses from polling
  useEffect(() => {
    // Don't process data if we've already reached the limit
    // Account will be marked ready and polling stopped, then message 3 will show before redirect
    if (hasReachedLimit) {
      setIsAccountReady(true);
      setIsPollingEnabled(false);
      return;
    }

    const data = promptDialogData;

    if (!data) {
      return;
    }

    const { messageType, id } = data;

    // Track which message we've already handled to prevent reopening dialog
    const messageKey = `${messageType}-${id}`;
    if (handledMessageTypeRef.current === messageKey) {
      return;
    }

    switch (messageType) {
      case 'IDENTITY_VERIFICATION_UPLOAD':
        // Mark as handled to prevent reopening
        handledMessageTypeRef.current = messageKey;

        // FullStory track event for GBG verification failed
        fullStoryTrackEvent('Verification | Failure', {
          provider: 'GBG',
        });

        // Stop any ongoing polling
        setIsPollingEnabled(false);

        // Open KYC dialog when GBG check fails
        // Keep LoadingSuccessStep visible, KYC dialog will open on top
        openKycDialog({ isRegistrationFlow: true });
        break;

      // Temporarily disabled for release — GreenID fallback dialog during registration
      // case 'GREEN_ID_DIALOG':
      //   if (!isGreenIdSupported) {
      //     debug.forceWarn('GREEN_ID_DIALOG received on a site without GreenID, ignoring');
      //     break;
      //   }
      //   handledMessageTypeRef.current = messageKey;
      //   setIsPollingEnabled(false);
      //   openKycDialog({ isRegistrationFlow: true, startMethod: 'greenid-fallback' });
      //   break;

      case 'DEPOSIT_LIMITS':
        // Mark as handled
        handledMessageTypeRef.current = messageKey;

        // FullStory track event for GBG verification passed
        fullStoryTrackEvent('Verification | Success', {
          provider: 'GBG',
        });

        // Stop any ongoing polling
        setIsPollingEnabled(false);

        // Mark account as ready after polling stops
        // Message 3 will be shown by the useEffect above after polling stops and message 2 has shown for 1.5s
        // Then redirect will happen after message 3 shows for 1.5s
        setIsAccountReady(true);

        // Don't set status to 'completed' here - let LoadingSuccessStep handle the flow
        // Status will be set to 'completed' after redirect happens
        break;

      case 'NO_DIALOG_TO_SHOW':
        // Continue polling (GBG still processing)
        break;

      default:
        // Log unknown message type
        debug.forceWarn('Unknown prompt dialog message type:', messageType);
        // Continue polling
        break;
    }
  }, [promptDialogData, hasReachedLimit, isPollingEnabled, router, setStatus, openKycDialog, fullStoryTrackEvent, config.legacySiteUrl, currentMessageIndex]);

  // Handle errors from polling
  useEffect(() => {
    if (promptDialogError && isPollingEnabled) {
      debug.forceError('Prompt dialog polling error:', promptDialogError);
    }
  }, [promptDialogError, isPollingEnabled]);

  // Cleanup: Stop polling when component unmounts
  useEffect(() => {
    return () => {
      setIsPollingEnabled(false);
    };
  }, []);

  const messages = isKenoGo ? [t('creatingAccount')] : [t('message1'), t('message2'), t('message3')];
  const currentMessage = messages[currentMessageIndex] || '';

  return (
    <div className="flex items-center justify-center min-h-[100dvh] w-full p-4" data-testid="loading-success-step">
      <div className="content-stretch flex flex-col gap-[20px] items-center relative shrink-0" data-track-container="Submitted" data-name="Message Container">
        {/* SpinnerIcon */}
        <div className="relative shrink-0 size-[32px]" data-name="Master icon">
          <div className="size-[32px] flex items-center justify-center">
            <Spinner size="regular" className="spinner-animate text-accent-green" />
          </div>
        </div>
        {/* LoadingMessagesContainer */}
        <div className="content-stretch flex flex-col gap-4 items-center justify-center relative shrink-0 text-foreground text-center w-full max-w-[295px]" data-name="Content">
          {/* LoadingMessage */}
          <p
            className={cn(
              'relative shrink-0 w-full font-normal transition-all duration-700 ease-in-out',
              isKenoGo ? 'text-2xl leading-8 text-foreground' : "display-s [font-variation-settings:'wdth'_0]",
              isMessageVisible ? (isKenoGo ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-0 text-gradient-animate') : 'opacity-0 translate-y-2 text-foreground-subtle'
            )}
            data-testid={`loading-message-${currentMessageIndex}`}
          >
            {currentMessage}
          </p>
          <p className={cn('relative shrink-0 w-full text-foreground-subtle', isKenoGo ? 'text-sm leading-relaxed font-normal' : "paragraph [font-variation-settings:'wdth'_0]")}>
            {t('subtitle')}
          </p>
        </div>
      </div>
    </div>
  );
}

export { LoadingSuccessStep };
export type { LoadingSuccessStepForm };
